import React, { useMemo } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { ImageHotspot } from '@thd-olt-component-react/image-hotspot';
import { VideoHeroImage } from '../../VideoHeroImage';

const { shape, number, bool } = proptypes;

// IF NOT IN A CAROUSEL, PROP WILL NOT BE PASSED AND THIS WILL DEFAULT TO 0
const getImageUrl = (imageData, isHotspotImage) => {
  if (isHotspotImage) {
    return imageData?.imageHotspot?.imageUrl || '';
  }
  return imageData?.damDownloadedContent?.url
    || imageData?.damContentSelector?.assetData?.[0]?.selectedImageUrl
    || '';
};

const BayImage = React.memo(({
  bayImageData,
  isHotspotImage,
  position,
  isVideo
}) => {
  const {
    mobilePreviewImage,
    previewImage,
    posterImage,
    desktopPosterImage,
    id,
    altText = '',
    title = '',
    shouldLoopVideo = true
  } = useMemo(
    () => bayImageData?.components?.[0] || bayImageData,
    [bayImageData]
  );

  const isMobileViewport = useBreakpoint('lg').lesser;
  const isLargerBayPosition = position === 1 || position === 3;

  const mobileImageUrl = useMemo(() => getImageUrl(mobilePreviewImage, isHotspotImage), [mobilePreviewImage]);
  const desktopImageUrl = useMemo(() => getImageUrl(previewImage, isHotspotImage), [previewImage]);

  const mobilePosterImageUrl = posterImage ? getImageUrl(posterImage, isHotspotImage) : null;
  const desktopPosterImageUrl = desktopPosterImage ? getImageUrl(desktopPosterImage, isHotspotImage) : null;

  const imageAltText = altText || title;
  const videoClasses = 'sui-w-full sui-object-cover sui-h-full';

  const hotspotId = useMemo(() => (isMobileViewport
    ? mobilePreviewImage?.imageHotspot?.id
    : previewImage?.imageHotspot?.id),
  [isMobileViewport, mobilePreviewImage, previewImage]
  );

  const bayMediaContent = isVideo
    ? (
      <VideoHeroImage
        mobileVideoUrl={mobileImageUrl}
        desktopVideoUrl={desktopImageUrl}
        mobilePosterImageUrl={mobilePosterImageUrl}
        desktopPosterImageUrl={desktopPosterImageUrl}
        altText={imageAltText}
        fullWidth
        isBentoHero
        videoClasses={videoClasses}
        shouldLoopVideo={shouldLoopVideo}
      />
    ) : (
      <picture style={{ width: '100%' }}>
        <source
          media="(min-width: 1024px)"
          alt={imageAltText}
          className="sui-w-full sui-h-full sui-top-0 sui-left-0"
          srcSet={desktopImageUrl}
          data-testid="bay-image-desktop"
        />
        <img
          src={mobileImageUrl}
          alt={imageAltText}
          className="sui-w-full sui-h-full sui-top-0 sui-left-0"
          data-testid="bay-image-img"
          fetchPriority={isLargerBayPosition ? 'high' : 'low'}
          width="1"
          height="1"
        />
      </picture>
    );

  if (isHotspotImage) {
    return (
      <ErrorBoundary name="image-hotspot">
        <QueryProvider cacheKey="image-hotspot">
          <span style={{ width: '100%' }}>
            <ImageHotspot
              componentId={hotspotId}
              componentClass="ImageHotspot"
              ssrImageData={{
                mobileImageSrc: mobileImageUrl,
                mobilePosterImageUrl,
                desktopPosterImageUrl,
                responsiveImages: [{
                  imageSrc: desktopImageUrl,
                  mediaQuery: '(min-width: 1024px)'
                }]
              }}
              enableSSR
              parentId={id}
              position={position}
              shouldLoopVideo={shouldLoopVideo}
            >
              {bayMediaContent}
            </ImageHotspot>
          </span>
        </QueryProvider>
      </ErrorBoundary>
    );
  }

  return bayMediaContent;
});

BayImage.propTypes = {
  bayImageData: shape({}).isRequired,
  position: number,
  isHotspotImage: bool,
  isVideo: bool
};

BayImage.defaultProps = {
  position: 0,
  isHotspotImage: false,
  isVideo: false
};

BayImage.displayName = 'BayImage';

export { BayImage };
